import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Third party components
import {Card, CardContent, Container, Grid, Stack, Typography} from '@mui/material';


// Shared components
import {
  Button, LineLoader, LoadingSpinner, ContainerWithCard,
  SubHeader
} from '@timmons-group/shared-react-components';

import AnyField from 'components/AnyField';

// Hooks
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

// TODO: Get this to shared-react-components once DB is updated
import { useLayout } from 'hooks/useData';

import { yupDate, yupObject, yupTrimStringMax, processLayout } from '@timmons-group/shared-react-components/helpers';
import { useCommunity, ACTIONS } from 'PAM/hooks/useCommunity';

import { object } from 'yup';

const CommunityPlanForm = () => {
  const { id, planId } = useParams();
  const isEdit = planId !== undefined;

  const rendered = () => {
    if (isEdit) {
      return <EditPlan communityId={id} planId={planId} />
    }

    return (
      <>
        <PlanForm
          formTitle="Add Plan Details"
          successUrl={`/pam/communityArea/${id}/plans`}
          cancelUrl={`/pam/communityArea/${id}`}
          communityId={id}
        />
      </>
    );
  }

  return rendered();
}

const EditPlan = ({communityId, planId}) => {
  const {communityState, dispatch} = useCommunity();
  const {loading, plans} = communityState;
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    // Force a refresh of the community data
    if(communityId) {
      dispatch({type: ACTIONS.SET_ID, payload: communityId});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  useEffect(() => {
    document.title = 'Edit Plan';
    if (!loading && plans) {
      const found = plans.find(p => p.planID === planId);
      if (found) {
        document.title = `Edit Plan ${found.name}`;
        setPlan(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  } , [loading]);

  if (loading) {
    return <LineLoader message="Loading Plan..." />
  }

  if (plans.length === 0 || !plan) {
    return <div>No plan found</div>
  }

  return (
    <>
      <PlanForm
        isEdit={true}
        plan={plan}
        formTitle="Edit Plan Details"
        cancelUrl={`/pam/communityArea/${communityId}/plans`}
        communityId={communityId}
      />
    </>
  );
}

EditPlan.propTypes = {
  communityId: PropTypes.string.isRequired,
  planId: PropTypes.string.isRequired,
}

const PlanForm = ({communityId, formTitle, cancelUrl, successUrl, isEdit, plan}) => {
  const [layout, layoutLoading] = useLayout('plan', 'newCommunityPlan');
  const { addOrEditPlan, communityState } = useCommunity();
  const { modifying } = communityState;

  const [sections, setSections] = useState([]);
  // Prepping for dynamic validations via config
  const [validations] = useState({
    contactInformation: yupTrimStringMax('Contact Information', false, 250),
    referenceUrl: yupTrimStringMax('Published Plan Reference URL', false, 1000),
    name: yupTrimStringMax('Name', true, 75).min(5),
    initiatedOn: yupDate('Planning Initiated On', true),
    activeOn: yupDate('Active as of', false).when('status', {
      is: (statusValue) => {
        return statusValue?.id === 2 || statusValue?.id === 3;
      },
      then: yupDate('Active as of', true)
    }),
    type: yupObject('Type', true),
    status: yupObject('Status', true),
  });

  // update the validation schema hookForm uses when the validation state changes
  const validationSchema = useMemo(
    () => {
      return object({...validations});
    },
    [validations]
  );

  const { control, handleSubmit, formState, watch, trigger, setValue, reset } = useForm({
    defaultValues: isEdit && plan ? plan : {
      name: '',
      initiatedOn: new Date(),
      activeOn: null,
      contactInformation: '',
      referenceUrl: '',
      status: null,
      type: { id: 1 },
    },
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  });

  const watchStatus = watch('status');
  useEffect(() => {
    const required = watchStatus?.id === 2 || watchStatus?.id === 3;

    const layoutSections = sections.map(section => {
      const fndField = section.fields.find(x => x.render.name === 'activeOn');
      if (fndField) {
        fndField.render.required = required;
      }
      return section;
    });

    setSections(layoutSections);

    if (formState.isSubmitted) {
      trigger('activeOn');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchStatus]);

  useEffect(() => {
    if (layoutLoading) {
      return;
    }
    const layoutSections = processLayout(layout);
    for (const section of layoutSections) {
      section.fields.forEach((field) => {
        // Preselect the first option if there is only one option
        if (field.render.choices?.length === 1) {
          setValue(field.render.name, field.render.choices[0]);
        }
      });
    }
    setSections(layoutSections);

    // We do this to cause any fields that might be watched to fire on initial load
    if (isEdit) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutLoading]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      streamID: communityId,
      type: parseInt(data.type?.id),
      status: parseInt(data.status?.id),
    };

    if (isEdit) {
      payload.planID = plan.planID;
    }

    addOrEditPlan(payload, isEdit, successUrl, cancelUrl);
  }

  const renderField = (field, control, index) => {
    return (
      <AnyField sx={{ marginTop: index ? '16px' : null }} key={index} layout={field.render} control={control} />
    )
  }

  // onSubmit is not called if the form is invalid
  // so we need to manually check for this
  const preSubmit = (evt) => {
    handleSubmit(onSubmit)(evt);
  }

  const createFormSection = (section, control, index) => {
    const nextCol = Math.ceil(section.fields.length / 2);
    const left = section.fields.slice(0, nextCol);
    const right = section.fields.slice(nextCol);

    return (
      <div key={index}>
        <Grid
          container
          spacing={{ xs: 1, sm: 2, md: 4 }}
        >
          <Grid item xs={6}>
            {left.map((field, index) => renderField(field, control, index))}
          </Grid>
          <Grid item xs={6}>
            {right.map((field, index) => renderField(field, control, index))}
          </Grid>
        </Grid>
      </div>
    )
  }

  const theForm = () => {
    return (
      <form>
        {sections.map((field, index) => {
          return createFormSection(field, control, index);
        })}
      </form>
    );
  }

  const rendered = () => {
    if (layoutLoading) {
      return (
        <ContainerWithCard>
          <LineLoader message="Loading..." />
        </ContainerWithCard>
      )
    }

    return (
      <>
        <SubHeader
          title={formTitle}
          rightRender={
            () =>
              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <Button color="error" href={cancelUrl} label="Cancel" />
                {isEdit && <Button color="primary" onClick={() => reset()} label={'Reset'} />}
                <Button color="secondary" onClick={preSubmit} label={isEdit ? 'Edit' : 'Save'} />
              </Stack>
          }
        />
        <LoadingSpinner isActive={modifying} />
        <Container sx={{ position: 'relative', marginTop: '16px' }} maxWidth={false}>
          <Card sx={{ position: 'relative' }}>
            <CardContent sx={{ paddingBottom: '0px' }}>
              <Typography variant="sectionHeader">Plan Details</Typography>
            </CardContent>
            <hr />
            <CardContent>
              <p>
                Please provide key details about the hazard mitigation plan that has been (or is being) developed for this Community.
              </p>
              {theForm()}
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  return rendered();
}

export default CommunityPlanForm;